/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import "./SwingieHome.css";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import { useAppContext } from "../../../../AppContext";
import {
  getEventScope,
  isCurrentScope,
  newGuestId,
  setActiveGuestId,
  storeGuestIdentity,
} from "../../../../eventIdentity";
import {
  openDatabase,
  getLastUpdateTime,
  updateLastUpdateTime,
  cleanUpOldEntries,
  getAllKeysFromDB,
  deleteSelectedImages,
} from "../../../../IndexedDBUtils";
import headerImage from "../../../../assets/images/Swingies-Header.png";
import { SERVER_URL } from "../../../../config/serverUrl";
import { getFaceClaimEvent } from "../../shared/faceClaimEvent";
import { releaseStoredClaim } from "../../shared/faceClaimClient";
import { fetchImageBatch, saveImageBatchToDB } from "../../shared/imageBatch";

// Controls whether the Swingies flow allows guests to build multiple
// characters at once. When enabled, buttons will be rendered for every
// possible team size from 1 up to the configured maximum. This allows us to
// easily adapt the experience for events that require a different amount of
// guests per turn.
const SWINGIE_MULTI_BUILD_ENABLED = true;
const SWINGIE_MULTI_BUILD_MAX_COUNT_DEFAULT = 3;
const SWINGIE_MULTI_BUILD_ALLOWED_MAX = 3;
const SWINGIE_MULTI_BUILD_ALLOWED_MIN = 1;
const DEFAULT_SINGLE_BUILD_COUNT = 1;

const clampSwingiesSelectionLimit = (limit) => {
  const numericLimit = Number(limit);

  if (!Number.isFinite(numericLimit)) {
    return SWINGIE_MULTI_BUILD_MAX_COUNT_DEFAULT;
  }

  return Math.min(
    SWINGIE_MULTI_BUILD_ALLOWED_MAX,
    Math.max(SWINGIE_MULTI_BUILD_ALLOWED_MIN, Math.floor(numericLimit))
  );
};

const buildSwingieSelectionOptions = (
  maxCount = SWINGIE_MULTI_BUILD_MAX_COUNT_DEFAULT
) => {
  if (!SWINGIE_MULTI_BUILD_ENABLED) {
    return [DEFAULT_SINGLE_BUILD_COUNT];
  }

  const sanitizedMaxCount = clampSwingiesSelectionLimit(maxCount);

  if (sanitizedMaxCount <= DEFAULT_SINGLE_BUILD_COUNT) {
    return [DEFAULT_SINGLE_BUILD_COUNT];
  }

  return Array.from({ length: sanitizedMaxCount }, (_, index) => index + 1);
};

const FACE_STORE_NAME = "FaceImages";
const BODY_STORE_NAME = "BodyImages";
const FACE_REFRESH_INTERVAL_SECONDS = 20;
const BODY_REFRESH_INTERVAL_SECONDS = 20;

// Latest body-upload operation across Home visits (see uploads_bodies below).
let bodiesUploadOp = 0;

const FACE_TTL = 1 * 1 * 1.5 * 60 * 1000; // 90 seconds
const BODY_TTL = 1 * 1 * 2 * 60 * 1000; // 2 minutes

const SwingieHomePage = ({ reFace, swingiesSelectionLimit }) => {
  const navigate = useNavigate();
  const { showLoading, hideLoading, acquireLoading } = useLoading();
  // eventName is set by EventRouteGate (validated route name), never here.
  const { eventName, setUserId } = useAppContext();
  const serverUrl = SERVER_URL;
  const businessSiteUrl = process.env.REACT_APP_BUSINESS_SITE_URL;
  const [timeLeft, setTimeLeft] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [graceMinutes, setGraceMinutes] = useState(0);
  const sanitizedSelectionLimit = useMemo(
    () =>
      clampSwingiesSelectionLimit(
        swingiesSelectionLimit ?? SWINGIE_MULTI_BUILD_MAX_COUNT_DEFAULT
      ),
    [swingiesSelectionLimit]
  );

  const swingieSelectionOptions = useMemo(
    () => buildSwingieSelectionOptions(sanitizedSelectionLimit),
    [sanitizedSelectionLimit]
  );
  const hasMultipleSelectionOptions = swingieSelectionOptions.length > 1;
  const singleSelectionValue =
    swingieSelectionOptions[0] ?? DEFAULT_SINGLE_BUILD_COUNT;

  // Parse a date string in the format "DD/MM/YYYY HH:mm"
  const parseDate = (dateStr) => {
    const [datePart, timePart] = dateStr.split(" ");
    const [day, month, year] = datePart.split("/");
    return new Date(`${year}-${month}-${day}T${timePart}:00`);
  };

  // Calculate remaining time until the given date string.
  const calculateTimeLeft = (t) => {
    const parsedDate = parseDate(t);
    const difference = parsedDate - new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }
    return timeLeft;
  };

  // Fetch images batch from server and store in IndexedDB. `isCurrent` turns
  // false once this page was unmounted or the event scope changed; the work
  // then stops, and no shared storage / freshness timestamp is written. IDB
  // writes already in flight only ever target this event's own database.
  // Resolves to { writtenFrom } (every entry this batch wrote is stamped at
  // or after it) once the batch was stored completely; otherwise (skipped by
  // the refresh guard, failed or cancelled) to undefined.
  const fetchAndStoreImagesBatch = async (
    folderType,
    storeName,
    ttl,
    isCurrent
  ) => {
    try {
      const lastUpdateTime = await getLastUpdateTime(eventName, storeName);
      if (!isCurrent()) return;
      const currentTime = new Date();
      const timeDifference = (currentTime - new Date(lastUpdateTime)) / 1000;

      if (timeDifference < ttl) {
        return;
      }
      const imageBatch = await fetchImageBatch(
        serverUrl,
        `${eventName}/${folderType}`
      );
      if (!isCurrent()) return;
      const serverPaths = Object.keys(imageBatch);

      // Get current paths in IndexedDB to find deleted images
      const keysFromDB = await getAllKeysFromDB(eventName, storeName);
      if (!isCurrent()) return;
      const pathsToDelete = keysFromDB.filter(
        (path) => !serverPaths.includes(path)
      );

      // Delete images that no longer exist on server
      if (pathsToDelete.length > 0) {
        await deleteSelectedImages(eventName, storeName, pathsToDelete);
      }

      const writtenFrom = new Date().toISOString();
      await saveImageBatchToDB(eventName, storeName, imageBatch, {
        isCurrent,
      });
      if (!isCurrent()) return;
      await updateLastUpdateTime(eventName, storeName);
      return { writtenFrom };
    } catch (error) {
      console.error(`Error fetching and storing ${folderType} images:`, error);
    }
  };

  // The TTL purge only follows a batch that stored its folder completely, and
  // never touches what that batch wrote (keepFrom). Run before the batch, it
  // emptied the store whenever the 20 s refresh guard skipped the refetch (the
  // galleries refresh the guard's timestamp on every no-change check without
  // re-stamping entries) or the batch failed, and the next gallery open
  // downloaded everything again (2026-10-06). After a complete batch it is a
  // safety net only: the batch already dropped what left the server and
  // re-stamped the rest.
  const purgeAfterBatch = async (batch, storeName, ttl, isCurrent) => {
    if (!batch || !isCurrent()) return;
    await cleanUpOldEntries(eventName, storeName, ttl, {
      keepFrom: batch.writtenFrom,
    });
  };

  // Initialize application data and preload images from the server or IndexedDB.
  useEffect(() => {
    if (!eventName) return undefined;
    const scope = getEventScope();
    let cancelled = false;
    const isCurrent = () => !cancelled && isCurrentScope(scope);
    const releaseLoading = acquireLoading();

    const initializeAndLoadData = async () => {
      await openDatabase(eventName);
      if (!isCurrent()) return;

      try {
        // Fetch event end time from the server.
        const response = await fetch(
          `${serverUrl}/api/get-end_event_time?event_name=${encodeURIComponent(
            eventName
          )}`
        );
        const data = await response.json();
        if (!isCurrent()) return;
        if (data.endTime) {
          setEndTime(data.endTime);
          setGraceMinutes(Number(data.graceMinutes) || 0);
          setTimeLeft(calculateTimeLeft(data.endTime));
        }
      } catch (error) {
        console.error("Error getting event end time:", error);
      }
      if (!isCurrent()) return;

      // A previous guest's editing hold is released (best effort) BEFORE the legacy fresh-guest clear
      // below wipes its handle; the server lease backstops a lost request.
      releaseStoredClaim(getFaceClaimEvent());
      sessionStorage.clear();
      localStorage.clear();

      // Each Home visit starts a new guest (shared generator with the gate).
      // Register and context are set together (the claim client reads the register, the approve
      // the context); the guarded storage write is only the reload carrier.
      const uuid = newGuestId();
      storeGuestIdentity(eventName, uuid);
      setActiveGuestId(uuid);
      sessionStorage.setItem("reFace", reFace);
      setUserId(uuid);

      const faceBatch = await fetchAndStoreImagesBatch(
        "faces/ready",
        FACE_STORE_NAME,
        FACE_REFRESH_INTERVAL_SECONDS,
        isCurrent
      );
      if (!isCurrent()) return;
      await purgeAfterBatch(faceBatch, FACE_STORE_NAME, FACE_TTL, isCurrent);
      if (!isCurrent()) return;
      hideLoading();
      releaseLoading();
      // The flag belongs to this operation: it is cleared when THIS body batch
      // ends, even if the page unmounted for a same-event page change, but
      // never after another event/visit took over or a newer Home started its
      // own batch (bodiesUploadOp moved on).
      const uploadOp = ++bodiesUploadOp;
      sessionStorage.setItem("uploads_bodies", true);
      try {
        const bodyBatch = await fetchAndStoreImagesBatch(
          "bodies",
          BODY_STORE_NAME,
          BODY_REFRESH_INTERVAL_SECONDS,
          isCurrent
        );
        await purgeAfterBatch(bodyBatch, BODY_STORE_NAME, BODY_TTL, isCurrent);
      } finally {
        if (uploadOp === bodiesUploadOp && isCurrentScope(scope)) {
          sessionStorage.setItem("uploads_bodies", false);
        }
      }
    };
    // Any failure (including before the inner try, e.g. openDatabase or
    // cleanUpOldEntries rejecting) is reported once and always releases THIS
    // owner's loading token; other owners' tokens are untouched.
    initializeAndLoadData()
      .catch((error) => {
        console.error("Error initializing event data:", error);
      })
      .finally(() => {
        releaseLoading();
      });

    return () => {
      cancelled = true;
      releaseLoading();
    };
  }, [eventName, serverUrl]);

  // Update the countdown timer every second.
  useEffect(() => {
    if (endTime) {
      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft(endTime));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [endTime]);

  // Handle number selection and navigate to the face gallery.
  const selectNumber = (number) => {
    sessionStorage.setItem("number", number);
    showLoading();
    navigate(`/${eventName}/face-gallery`);
  };

  // Format the remaining time as a string.
  const formatTimeLeft = () => {
    if (!timeLeft) return "00:00:00";
    const days = timeLeft.days > 0 ? (timeLeft.days === 1 ? `יום אחד ` : `${timeLeft.days} ימים `) : "";
    return `${days}${String(timeLeft.hours).padStart(2, "0")}:${String(
      timeLeft.minutes
    ).padStart(2, "0")}:${String(timeLeft.seconds).padStart(2, "0")}`;
  };

  // Universal end grace (2026-08-24): past the scheduled end the event stays
  // live for graceMinutes server-side. The timer used to just vanish; now the
  // guest sees "האירוע הסתיים" — blinking through the grace window (photos
  // still go through), steady once it truly closed.
  const endedPhase = () => {
    if (!endTime) return null;
    const end = parseDate(endTime);
    if (!end || Number.isNaN(end.getTime())) return null;
    const now = Date.now();
    if (now < end.getTime()) return null;
    const graceMs = (graceMinutes || 0) * 60 * 1000;
    return now < end.getTime() + graceMs ? "grace" : "over";
  };

  return (
    <div className="swingie-home-container">
      <div className="swingie-home-title">
        <img src={headerImage} alt="Title" />
      </div>
      <div className="swingie-home-logo-container">
        {businessSiteUrl ? (
          <a
            href={businessSiteUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={process.env.PUBLIC_URL + "/logoTrim_swingies.png"}
              alt="Logo"
              className="swingie-home-logo-image"
            />
          </a>
        ) : (
          <img
            src={process.env.PUBLIC_URL + "/logoTrim_swingies.png"}
            alt="Logo"
            className="swingie-home-logo-image"
          />
        )}
      </div>
      {endTime && timeLeft && timeLeft.hours !== undefined && (
        <div className="swingie-home-remaining-text rtl">
          נותרו {formatTimeLeft()} לעיצוב סווינגי משלך.
        </div>
      )}
      {endedPhase() && (
        <div
          className={`swingie-home-remaining-text swingie-home-ended rtl${
            endedPhase() === "grace" ? " swingie-home-ended-blink" : ""
          }`}
        >
          האירוע הסתיים
        </div>
      )}
      <div className="swingie-home-content">
        {hasMultipleSelectionOptions && (
          <h2 className="swingie-home-subtitle rtl">כמה אנשים אתם?</h2>
        )}
        <div className="swingie-home-button-container">
          {hasMultipleSelectionOptions ? (
            swingieSelectionOptions.map((option) => (
              <button
                key={option}
                className="swingie-home-number-button"
                onClick={() => selectNumber(option)}
              >
                {option}
              </button>
            ))
          ) : (
            <button
              className="swingie-home-number-button"
              onClick={() => selectNumber(singleSelectionValue)}
            >
              התחל
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SwingieHomePage;
